import React, { useState, useEffect } from 'react';
import { getResults, saveResults, getRaceDnfs, isRaceLocked, getRaceDrivers, syncFinalRaceResults, syncRaceResultsFromHistorical } from '../../api';
import DriverSelector from '../../components/DriverSelector';

const EDITABLE_START_POSITION = 4;
const EDITABLE_RESULT_COUNT = 7;

function toEditableResults(results) {
    const safe = Array.isArray(results) ? results : [];
    return Array.from({ length: EDITABLE_RESULT_COUNT }, (_, index) => safe[EDITABLE_START_POSITION - 1 + index] ?? null);
}

export default function AdminResults({ currentRace, drivers, racesMap, setMessage, lockState, setLockState }) {
    const [results, setResults] = useState(Array(EDITABLE_RESULT_COUNT).fill(null));
    const [dnfs, setDnfs] = useState([]);
    const [noDnf, setNoDnf] = useState(false);
    const [raceDrivers, setRaceDrivers] = useState([]);

    useEffect(() => {
        if (!currentRace) return;
        Promise.all([getResults(currentRace), getRaceDnfs(currentRace), isRaceLocked(currentRace), getRaceDrivers(currentRace)])
            .then(([r, d, locked, rdResp]) => {
                const hasResults = r && r.length > 0;
                setResults(hasResults ? toEditableResults(r) : Array(EDITABLE_RESULT_COUNT).fill(null));
                const loadedDnfs = Array.isArray(d) ? d : [];
                setDnfs(loadedDnfs);
                setNoDnf(hasResults && loadedDnfs.length === 0);
                setLockState(hasResults ? !!locked : false);
                // Use race-specific drivers if available
                const rdList = rdResp?.drivers || [];
                setRaceDrivers(rdList.length > 0 ? rdList : drivers);
            })
            .catch((e) => {
                console.error('Error loading race data in admin', e);
                setResults(Array(EDITABLE_RESULT_COUNT).fill(null));
                setDnfs([]);
            });
    }, [currentRace, setLockState]);

    function update(i, v) {
        const next = [...results];
        next[i] = v;
        setResults(next);
    }

    function toggleDnf(driverId) {
        setNoDnf(false);
        setDnfs((prev) =>
            prev.includes(driverId) ? prev.filter((id) => id !== driverId) : [...prev, driverId]
        );
    }

    function toggleNoDnf() {
        setNoDnf((prev) => {
            if (!prev) setDnfs([]);
            return !prev;
        });
    }

    async function handleSave() {
        if (!currentRace) {
            return setMessage('Select a race to publish results');
        }
        if (results.some((r) => r == null)) {
            return setMessage('Please fill positions 4-10 before publishing');
        }
        if (!noDnf && dnfs.length === 0) {
            return setMessage('Please select DNF drivers or check "No DNF"');
        }

        try {
            const saved = await saveResults(results, currentRace, dnfs, EDITABLE_START_POSITION);
            setResults(Array.isArray(saved) ? toEditableResults(saved) : results);
            setMessage('Results published for ' + (racesMap[String(currentRace)] || currentRace));
        } catch (err) {
            console.error('Error saving results', err);
            setMessage('Error publishing results');
        }
    }

    async function handleRefreshFinalResults() {
        if (!currentRace) {
            return setMessage('Select a race first');
        }

        try {
            const payload = await syncFinalRaceResults(currentRace);
            const syncedRows = Array.isArray(payload?.rows) ? payload.rows : [];
            const nonDnf = syncedRows
                .filter((row) => row.status == null || String(row.status).toUpperCase() !== 'DNF')
                .sort((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER))
                .reduce((acc, row) => {
                    if (row.position != null) acc[Number(row.position) - 1] = row.driver_id;
                    return acc;
                }, []);
            const syncedDnfs = syncedRows
                .filter((row) => String(row.status || '').toUpperCase() === 'DNF')
                .map((row) => row.driver_id);

            setResults(nonDnf.length ? toEditableResults(nonDnf) : Array(EDITABLE_RESULT_COUNT).fill(null));
            setDnfs(syncedDnfs);
            setNoDnf(syncedDnfs.length === 0);
            setMessage(`Final race results synced (${payload?.synced || syncedRows.length} rows)`);
        } catch (err) {
            console.error('Error syncing final race results', err);
            setMessage(err.message || 'Unable to sync final race results');
        }
    }

    async function handlePopulateFromHistorical2025() {
        if (!currentRace) {
            return setMessage('Select a race first');
        }

        try {
            const payload = await syncRaceResultsFromHistorical(currentRace, 2025);
            const syncedRows = Array.isArray(payload?.rows) ? payload.rows : [];
            const nonDnf = syncedRows
                .filter((row) => row.status == null || String(row.status).toUpperCase() !== 'DNF')
                .sort((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER))
                .reduce((acc, row) => {
                    if (row.position != null) acc[Number(row.position) - 1] = row.driver_id;
                    return acc;
                }, []);
            const syncedDnfs = syncedRows
                .filter((row) => String(row.status || '').toUpperCase() === 'DNF')
                .map((row) => row.driver_id);

            setResults(nonDnf.length ? toEditableResults(nonDnf) : Array(EDITABLE_RESULT_COUNT).fill(null));
            setDnfs(syncedDnfs);
            setNoDnf(syncedDnfs.length === 0);
            setMessage(`Populated results from 2025 historical data (${payload?.synced || syncedRows.length} rows)`);
        } catch (err) {
            console.error('Error syncing historical 2025 results', err);
            setMessage(err.message || 'Unable to populate results from 2025 historical data');
        }
    }

    return (
        <>
            <h5 className="mb-3">Official Results</h5>
            <div className="card mb-3">
                <div className="card-body">
                    <div className="mb-4">
                        <label className="form-label">Official Positions 4-10 (use driver selector)</label>
                        <div className="list-group">
                            {results.map((r, i) => (
                                <div className="list-group-item d-flex align-items-center gap-3" key={i}>
                                    <label className="form-label mb-0" style={{ minWidth: 36 }}>#{i + EDITABLE_START_POSITION}</label>
                                    <div className="flex-grow-1">
                                        <DriverSelector value={r != null ? r : null} onChange={(v) => update(i, v)} drivers={raceDrivers.length > 0 ? raceDrivers : undefined} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="form-label fw-semibold">DNF Drivers</label>
                        <p className="text-muted small mb-2">Select all drivers who did not finish the race.</p>
                        <div className="border rounded p-2" style={{ maxHeight: 260, overflowY: 'auto' }}>
                            <div className="form-check mb-1 border-bottom pb-1">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="dnf-none"
                                    checked={noDnf}
                                    disabled={dnfs.length > 0}
                                    onChange={toggleNoDnf}
                                />
                                <label className="form-check-label fw-semibold" htmlFor="dnf-none">
                                    No DNF
                                </label>
                            </div>
                            {raceDrivers.length === 0 && <span className="text-muted small">Loading drivers…</span>}
                            {raceDrivers.map((d) => (
                                <div className="form-check" key={d.id}>
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`dnf-${d.id}`}
                                        checked={dnfs.includes(d.id)}
                                        disabled={noDnf}
                                        onChange={() => toggleDnf(d.id)}
                                    />
                                    <label className="form-check-label" htmlFor={`dnf-${d.id}`}>
                                        {d.code} — {d.first_name} {d.last_name}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="d-flex gap-2 mt-1">
                        <button className="btn btn-success" type="button" onClick={handleSave}>
                            Publish Results for Selected Race
                        </button>
                        <button className="btn btn-outline-primary" type="button" onClick={handleRefreshFinalResults}>
                            Refresh Final Results from API
                        </button>
                        <button className="btn btn-outline-secondary" type="button" onClick={handlePopulateFromHistorical2025}>
                            Populate from 2025 Historical
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}
